/**
 * The findings presentation.
 *
 * Three lists, kept structurally separate, because that separation IS the
 * specified behaviour rather than a layout preference:
 *
 *  - Ranked findings — the product.
 *  - "Cannot score — rate unknown" — unknown exposure is null, never zero, and
 *    such findings are shown apart rather than ranked as worthless.
 *    SPEC-0002 §3.
 *  - Unmatched roles — data quality, "visually separated from true
 *    reconciliation findings" so a long list cannot teach users to skip the
 *    real one. ADR-0010, Consequences.
 */
import type { Finding, UnmatchedRoleItem } from '@picklii/recon';
import { FINDING_LABELS, RATE_SOURCE_LABELS, bandClass, hours, money, percent, severity, signedHours } from '../lib/format';

export function SeverityBadge({ band }: { band: Finding['severityBand'] }) {
	return <span className={`badge badge-sm ${bandClass(band)}`}>{band ?? 'Unscored'}</span>;
}

/** An estimated figure is never presented as a measured one. SPEC-0002 §3. */
function Exposure({ finding }: { finding: Finding }) {
	if (finding.exposureCents === null) {
		return <span className="text-base-content/70 italic">Unknown</span>;
	}
	return (
		<span className="tabular-nums">
			{money(finding.exposureCents)}
			{finding.rateEstimated && (
				<span
					className="badge badge-ghost badge-xs ms-2 align-middle"
					title={RATE_SOURCE_LABELS[finding.rateSource]}>
					est.
				</span>
			)}
		</span>
	);
}

export function FindingsTable({ findings }: { findings: readonly Finding[] }) {
	if (findings.length === 0) {
		return (
			<div className="alert alert-success">
				<span>No mismatches. Sold hours and PSA budget agree on every line.</span>
			</div>
		);
	}

	return (
		<div className="overflow-x-auto">
			<table className="table">
				<thead>
					<tr>
						<th>Severity</th>
						<th>Project</th>
						<th>Client</th>
						<th>Role</th>
						<th>Finding</th>
						<th className="text-right">Sold</th>
						<th className="text-right">Budgeted</th>
						<th className="text-right">Δ hours</th>
						<th className="text-right">Burn</th>
						<th className="text-right">Exposure</th>
						<th className="text-right" title="Exposure weighted by burn and direction. A severity ranking, not a dollar amount.">
							Severity
						</th>
					</tr>
				</thead>
				<tbody>
					{findings.map((f) => (
						<tr key={f.id}>
							<td>
								<SeverityBadge band={f.severityBand} />
							</td>
							<td className="font-medium">{f.projectId}</td>
							<td>{f.clientName}</td>
							<td>
								{f.roleRaw}
								{f.roleRaw.trim().toLowerCase() !== f.roleNormalized && (
									<span className="text-base-content/70 block text-xs">{f.roleNormalized}</span>
								)}
							</td>
							<td>
								<span className="badge badge-ghost badge-sm">{FINDING_LABELS[f.type]}</span>
							</td>
							<td className="text-right tabular-nums">{hours(f.soldHours)}</td>
							<td className="text-right tabular-nums">{hours(f.budgetedHours)}</td>
							<td className="text-right tabular-nums">{signedHours(f.deltaHours)}</td>
							<td className="text-right tabular-nums">{percent(f.burnPct)}</td>
							<td className="text-right">
								<Exposure finding={f} />
							</td>
							{/* "Severity", never "dollars at risk". ADR-0009. */}
							<td className="text-right font-medium tabular-nums">{severity(f.severityScore)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export function UnscoredFindings({ findings }: { findings: readonly Finding[] }) {
	if (findings.length === 0) return null;

	return (
		<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs">
			<div className="card-body gap-4">
				<div>
					<h2 className="card-title text-base">Cannot score — rate unknown</h2>
					<p className="text-base-content/70 text-sm">
						No rate could be derived for these, so their dollar exposure is unknown. Unknown is
						not zero: these may be larger than anything ranked above.
					</p>
				</div>
				<div className="overflow-x-auto">
					<table className="table table-sm">
						<thead>
							<tr>
								<th>Project</th>
								<th>Client</th>
								<th>Role</th>
								<th>Finding</th>
								<th className="text-right">Budgeted</th>
								<th className="text-right">Burn</th>
							</tr>
						</thead>
						<tbody>
							{findings.map((f) => (
								<tr key={f.id}>
									<td className="font-medium">{f.projectId}</td>
									<td>{f.clientName}</td>
									<td>{f.roleRaw}</td>
									<td>{FINDING_LABELS[f.type]}</td>
									<td className="text-right tabular-nums">{hours(f.budgetedHours)}</td>
									<td className="text-right tabular-nums">{percent(f.burnPct)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	);
}

export function UnmatchedRoles({ items }: { items: readonly UnmatchedRoleItem[] }) {
	if (items.length === 0) return null;

	return (
		<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs">
			<div className="card-body gap-4">
				<div>
					<h2 className="card-title text-base">Unmatched roles — data quality</h2>
					<p className="text-base-content/70 text-sm">
						These role names appear on one side with no counterpart on the other. Picklii will not
						guess a pairing: a silently wrong join is the exact failure this product exists to
						catch. Confirm an alias to join them, or treat the gap as a real finding.
					</p>
				</div>
				<div className="overflow-x-auto">
					<table className="table table-sm">
						<thead>
							<tr>
								<th>Project</th>
								<th>Client</th>
								<th>In sold hours</th>
								<th>In PSA budget</th>
							</tr>
						</thead>
						<tbody>
							{items.map((item, index) => (
								<tr key={`${item.projectId}-${index}`}>
									<td className="font-medium">{item.projectId}</td>
									<td>{item.clientName}</td>
									<td>{item.soldRoleRaw ?? <span className="text-base-content/70">—</span>}</td>
									<td>{item.budgetRoleRaw ?? <span className="text-base-content/70">—</span>}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	);
}
